/**
 * Lightweight daily-reminder helper using the browser Notification API.
 *
 * NOTE: This only fires notifications when the app's tab is open at the
 * scheduled time. True background push (notifications when the site is
 * closed) requires a service worker + VAPID + backend cron — that can be
 * added later as Phase 2.
 */

const REMINDER_KEY = 'graceRemindersEnabled';
const REMINDER_HOUR_KEY = 'graceReminderHour';
const LAST_FIRED_KEY = 'graceReminderLastFiredDate';
const DEFAULT_HOUR = 10; // 10am local time

let scheduledTimeoutId = null;

const todayKey = () => new Date().toISOString().split('T')[0];

const msUntilNextFire = (hour) => {
  const now = new Date();
  const target = new Date();
  target.setHours(hour, 0, 0, 0);
  if (target <= now) target.setDate(target.getDate() + 1);
  return target.getTime() - now.getTime();
};

const fireReminder = () => {
  if (typeof window === 'undefined' || !('Notification' in window)) return;
  if (Notification.permission !== 'granted') return;
  if (localStorage.getItem(LAST_FIRED_KEY) === todayKey()) return;

  try {
    const notif = new Notification('I May Help You', {
      body: "It's okay to not be okay today. How are you, really?",
      tag: 'grace-checkin',
      icon: '/favicon.ico',
    });
    notif.onclick = () => {
      window.focus();
      notif.close();
    };
    localStorage.setItem(LAST_FIRED_KEY, todayKey());
  } catch {
    // Silent — notification failure shouldn't break the app.
  }
};

const scheduleNextReminder = () => {
  if (typeof window === 'undefined' || !('Notification' in window)) return;
  if (localStorage.getItem(REMINDER_KEY) !== 'true') return;
  if (scheduledTimeoutId) clearTimeout(scheduledTimeoutId);

  const hour = Number(localStorage.getItem(REMINDER_HOUR_KEY)) || DEFAULT_HOUR;
  const delay = msUntilNextFire(hour);

  scheduledTimeoutId = setTimeout(() => {
    fireReminder();
    scheduleNextReminder();
  }, delay);
};

export const remindersEnabled = () =>
  typeof window !== 'undefined' &&
  'Notification' in window &&
  Notification.permission === 'granted' &&
  localStorage.getItem(REMINDER_KEY) === 'true';

export const enableDailyReminders = async () => {
  if (typeof window === 'undefined' || !('Notification' in window)) {
    return { ok: false, reason: 'unsupported' };
  }
  let perm = Notification.permission;
  if (perm === 'default') {
    perm = await Notification.requestPermission();
  }
  if (perm !== 'granted') {
    return { ok: false, reason: 'denied' };
  }
  localStorage.setItem(REMINDER_KEY, 'true');
  if (!localStorage.getItem(REMINDER_HOUR_KEY)) {
    localStorage.setItem(REMINDER_HOUR_KEY, String(DEFAULT_HOUR));
  }
  scheduleNextReminder();
  return { ok: true };
};

export const initReminderSchedule = () => {
  if (typeof window === 'undefined' || !('Notification' in window)) return;
  if (Notification.permission === 'granted' && localStorage.getItem(REMINDER_KEY) === 'true') {
    scheduleNextReminder();
  }
};
